import { useState, useEffect, useMemo, useCallback } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  api,
  type CreateServerResponse,
  type DeploymentMode,
  type Server,
  type ServerAuditReport,
  type MagentoInstallationType,
  type MagentoDatabaseStrategy,
  type MagentoDatabaseSource,
  type MagentoCryptKeyStrategy,
  type MagentoCacheStrategy,
  type MagentoSearchEngine,
  type MagentoSearchMode,
  type MagentoSearchStrategy,
  type MagentoMediaStrategy,
  type MagentoCustomEnvVar,
} from '../api';
import { slugify } from '../hooks';
import { AgentInstallPanel, agentConnected, type AgentInstallState } from '../components/AgentInstallPanel';
import { PlatformChoice } from '../components/wordpress/PlatformChoice';
import { GitConnectModal } from '../components/repository/GitConnectModal';
import { GitProviderChoice, ManualRepositoryUrl, ProviderConnectPrompt, RepositoryList } from '../components/repository/RepositorySelector';
import { useGitRepositories } from '../components/repository/useGitRepositories';
import { repositoryFields } from '../components/repository/repositoryFields';
import type { RepositorySelection } from '../components/repository/repositoryFields';
import { Callout, Checkbox, Field, FieldError, Icon, IconButton, Input, OptionCard, OptionGroup, Section, Segmented, Select, Textarea } from '../components/primitives';
import { OnboardingChoice, OnboardingLayout } from '../components/OnboardingLayout';
import { StatusBadge } from '../components/ui';
import { readinessStatusLabel, serverStatusLabel } from '../labels';

// What an empty branch means: the API asks the provider once it can.
const REPOSITORY_DEFAULT_BRANCH = "Repository's default branch";

const DEFAULT_PHASES = [
  { id: "fetch", name: "Fetch repository", cmd: "git fetch origin <branch> --depth=1", required: true, enabled: true },
  { id: "composer", name: "Install Composer dependencies", cmd: "composer install --no-dev --prefer-dist --optimize-autoloader", required: true, enabled: true },
  { id: "di", name: "Build generated code and compile DI", cmd: "bin/magento setup:di:compile", required: true, enabled: true },
  { id: "static", name: "Deploy static content", cmd: "bin/magento setup:static-content:deploy -f en_US de_DE --jobs=4", required: true, enabled: true },
  { id: "maint", name: "Enable maintenance mode only if the database needs upgrading", cmd: "bin/magento maintenance:enable (only when setup:db:status reports pending changes)", required: false, enabled: true },
  { id: "upgrade", name: "Upgrade the database if required", cmd: "bin/magento setup:upgrade --keep-generated", required: false, enabled: true },
  { id: "switch", name: "Switch the release atomically", cmd: "ln -sfn releases/<n> current", required: true, enabled: true },
  { id: "cache", name: "Flush the Magento cache", cmd: "bin/magento cache:flush", required: true, enabled: true },
  { id: "health", name: "Run the health checks; roll back if one fails", cmd: "the paths configured on the next step", required: true, enabled: true },
];

interface HealthCheckItem {
  id: string;
  name: string;
  url: string;
  expect: string;
  enabled: boolean;
  fixed: boolean;
}

/**
 * CreateProjectPage is the Magento wizard, on the shared onboarding layout
 * (CO-90): the steps on a rail at the left, one footer (Cancel and Back
 * left, Continue right), and validation when Continue is pressed — a
 * message under each field that needs attention, with the first one
 * focused.
 */
export function CreateProjectPage() {
  const navigate = useNavigate();

  // Wizard step (1..6)
  const [step, setStep] = useState(1);

  // Architecture. A headless project's frontends are not configured here:
  // they are added on the project page once the Magento setup has run
  // (CO-73), one per storefront, each with its own repository.
  const [architecture, setArchitecture] = useState<"traditional" | "headless">("traditional");

  // Magento Runtime & Infrastructure Configuration State
  const [installationType, setInstallationType] = useState<MagentoInstallationType>("new_installation");
  const [mageMode, setMageMode] = useState<"production" | "developer" | "default">("production");

  // Database Configuration
  const [dbStrategy, setDbStrategy] = useState<MagentoDatabaseStrategy>("create_managed");
  const [dbHost, setDbHost] = useState("127.0.0.1");
  const [dbPort, setDbPort] = useState("3306");
  const [dbName, setDbName] = useState("magento");
  const [dbUser, setDbUser] = useState("magento");
  const [dbPassword, setDbPassword] = useState("");
  const [dbTablePrefix, setDbTablePrefix] = useState("");
  const [dbTlsMode, setDbTlsMode] = useState("disable");
  // A dump is imported after setup, from a file already on the server
  // (Recovery → Import an existing store); nothing is uploaded here.
  const dbImportSource: MagentoDatabaseSource = "existing_file_on_server";

  // Crypt Key / Encryption Key
  const [cryptKeyStrategy, setCryptKeyStrategy] = useState<MagentoCryptKeyStrategy>("generate_new");
  const [cryptKeyCustom, setCryptKeyCustom] = useState("");

  // URLs & Admin Routing
  const [adminFrontName, setAdminFrontName] = useState("admin");

  // Cache & Sessions
  const [cacheStrategy, setCacheStrategy] = useState<MagentoCacheStrategy>("commerceops_managed");
  const cacheHost = "127.0.0.1";
  const cachePort = "6379";
  const defaultCacheDb = 0;
  const pageCacheDb = 1;
  const sessionDb = 2;
  const cacheTls = false;

  // Catalog search (CO-47). Discovered from the repository when the setup
  // plan is generated: Commerce Ops provisions OpenSearch/Elasticsearch
  // only when the code needs it. The engine/strategy below are the
  // placement preference for that case, or the explicit override.
  const [searchMode, setSearchMode] = useState<MagentoSearchMode>("discovered");
  const [searchEngine, setSearchEngine] = useState<MagentoSearchEngine>("opensearch");
  const [searchStrategy, setSearchStrategy] = useState<MagentoSearchStrategy>("commerceops_managed");
  const searchHost = "127.0.0.1";
  const searchPort = "9200";
  const searchScheme = "http";
  const searchUsername = "";
  const [searchIndexPrefix, setSearchIndexPrefix] = useState("magento2_");

  // RabbitMQ / Message Queue
  const [queueEnabled, setQueueEnabled] = useState(true);
  const queueStrategy: MagentoCacheStrategy = "commerceops_managed";
  const queueHost = "127.0.0.1";
  const queuePort = "5672";
  const queueUsername = "guest";
  const queueVirtualHost = "/";
  const queueTls = false;

  // Media Storage

  // Locale, Timezone, Currency
  const [defaultLocale, setDefaultLocale] = useState("en_US");
  const [timezone, setTimezone] = useState("UTC");
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const defaultDisplayCurrency = baseCurrency;

  // Scoped Custom Magento Env Vars
  const magentoCustomEnvVars: MagentoCustomEnvVar[] = [];

  const [repo, setRepo] = useState<string>("");
  const [customRepoUrl, setCustomRepoUrl] = useState("");
  const [useCustomRepo, setUseCustomRepo] = useState(false);
  // Empty until a repository is chosen: the provider reports the branch it
  // actually defaults to, and an empty value asks the API to resolve it.
  // Starting at "main" quietly submitted a branch nobody had confirmed.
  const [branch, setBranch] = useState("");
  const [magentoRoot, setMagentoRoot] = useState("/");

  // Step 1: Git provider and repository list, shared with WordPress
  // onboarding (CO-69). Magento preselects the first repository listed.
  const git = useGitRepositories({
    onRepositoriesLoaded: (mapped, replace) => {
      const first = mapped[0];
      if (first && (replace || !repo || !mapped.some((m) => m.fullName === repo))) {
        setRepo(first.fullName);
        const firstBranch = first.branches[0];
        if (firstBranch) setBranch(firstBranch);
      }
    },
  });
  const { provider, providerAccounts, providerTokens, connectedProviders, repositories } = git;

  // Server step. The recommended way is a server connected through the
  // Commerce Ops Agent (pick one, or connect a new one right here); SSH is
  // the secondary path for a Magento installation already running natively
  // on the server.
  const [serverMethod, setServerMethod] = useState<"agent" | "ssh">("agent");
  const [servers, setServers] = useState<Server[]>([]);
  const [serversLoading, setServersLoading] = useState(false);
  const [serversError, setServersError] = useState<string | null>(null);
  const [selectedServerId, setSelectedServerId] = useState<string>("");
  const [connectingNewServer, setConnectingNewServer] = useState(false);
  const [newServerName, setNewServerName] = useState("");
  const [creatingServer, setCreatingServer] = useState(false);
  const [newServer, setNewServer] = useState<CreateServerResponse | null>(null);
  const [newServerState, setNewServerState] = useState<AgentInstallState>("waiting");
  // SSH path
  const [host, setHost] = useState("");
  const [port, setPort] = useState("22");
  const [user, setUser] = useState("");
  const [authMode, setAuthMode] = useState<"key" | "password">("key");
  const [privateKey, setPrivateKey] = useState("");
  const [password, setPassword] = useState("");
  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "ok" | "error">("idle");
  const [testLatency, setTestLatency] = useState<number | null>(null);
  const [testError, setTestError] = useState<string>("");

  // Step 3: Detection
  const [detectProgress, setDetectProgress] = useState(0);
  const [detectDone, setDetectDone] = useState(false);
  const [auditReport, setAuditReport] = useState<ServerAuditReport | null>(null);
  // The Magento directory on the server. Filled from what the inspection
  // found; editable, because only the operator knows for certain. Never
  // derived from the project's name — a customer's store lives where it
  // lives, not at /var/www/<our slug>.
  const [serverMagentoRoot, setServerMagentoRoot] = useState("");
  const [, setInspectLoading] = useState(false);
  const [inspectError, setInspectError] = useState<string | null>(null);
  // Bumped by Retry: the inspection reads the server again.
  const [inspectAttempt, setInspectAttempt] = useState(0);

  // Step 4: Deployment Config
  const [sharedPaths, setSharedPaths] = useState(["pub/media", "var", "app/etc/env.php", "app/etc/config.php"]);
  const [newShared, setNewShared] = useState("");

  // Step 5: Health checks – URLs are updated automatically when the domain is set
  const [healthChecks, setHealthChecks] = useState<HealthCheckItem[]>([
    { id: "hc1", name: "Homepage", url: "/", expect: "HTTP 200", enabled: true, fixed: true },
    { id: "hc2", name: "Magento GraphQL", url: "/graphql", expect: "HTTP 200", enabled: true, fixed: true },
    { id: "hc3", name: "Health endpoint", url: "/health_check.php", expect: "HTTP 200", enabled: true, fixed: true },
    { id: "hc4", name: "Checkout", url: "/checkout", expect: "HTTP 200", enabled: true, fixed: false },
  ]);

  // Step 6: Review & Final
  const [projectName, setProjectName] = useState("");
  const [environment, setEnvironment] = useState<"Production" | "Staging">("Production");
  const [domain, setDomain] = useState("");
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Incremented on every failed Continue: the layout focuses the first
  // field with a message (CO-90).
  const [invalidAttempt, setInvalidAttempt] = useState(0);
  // A field's message goes once the field is changed; the rest stay until
  // the next Continue.
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // When repo or customRepoUrl changes, derive initial name, domain, branch
  useEffect(() => {
    let repoName = "";
    if (useCustomRepo && customRepoUrl) {
      const match = customRepoUrl.match(/\/([^/]+?)(?:\.git)?$/);
      if (match && match[1]) {
        repoName = match[1];
      }
    } else if (repo && !useCustomRepo) {
      const parts = repo.split("/");
      repoName = parts[1] || parts[0] || "project";
    }

    if (repoName) {
      const derivedName = repoName
        .split(/[-_]/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
        .replace(" Magento", "");
      setProjectName((prev) => prev || derivedName);

      if (!useCustomRepo) {
        const found = repositories.find((r) => r.fullName === repo);
        if (found && found.branches.length > 0 && found.branches[0]) {
          setBranch(found.branches[0]);
        }
      }
    }
  }, [repo, repositories, useCustomRepo, customRepoUrl]);

  // Update health check URLs when domain changes
  useEffect(() => {
    if (domain && domain.trim()) {
      const activeHost = domain.trim();
      setHealthChecks((prev) =>
        prev.map((hc) => {
          // Handle path-only initial values (e.g. "/graphql")
          if (hc.url.startsWith('/')) {
            return { ...hc, url: `https://${activeHost}${hc.url}` };
          }
          try {
            const urlObj = new URL(hc.url);
            urlObj.host = activeHost;
            return { ...hc, url: urlObj.toString() };
          } catch {
            return { ...hc, url: `https://${activeHost}/` };
          }
        })
      );
    }
  }, [domain]);

  // Detection items specification strictly from real audit report
  const detectionItems = useMemo(() => {
    if (!auditReport) {
      return [];
    }
    const items = [];
    if (auditReport.magento_version) {
      items.push({ name: "Magento", value: auditReport.magento_version, badge: "success", label: "Detected" });
    }
    if (auditReport.php_version) {
      items.push({ name: "PHP", value: auditReport.php_version, badge: "success", label: "Compatible" });
    }
    if (auditReport.composer_version) {
      items.push({ name: "Composer", value: auditReport.composer_version, badge: "success", label: "Detected" });
    }
    if (auditReport.mysql_version) {
      items.push({ name: "Database", value: auditReport.mysql_version, badge: "success", label: "Detected" });
    }
    if (auditReport.redis_detected) {
      items.push({ name: "Redis", value: "Active", badge: "success", label: "Detected" });
    }
    if (auditReport.varnish_detected) {
      items.push({ name: "Varnish", value: "Active", badge: "success", label: "Detected" });
    }
    if (auditReport.opensearch_detected) {
      items.push({ name: "Search service", value: "OpenSearch or Elasticsearch on port 9200 (used only if the code needs it)", badge: "success", label: "Detected" });
    }
    if (auditReport.disk_free_bytes) {
      items.push({ name: "Disk storage", value: `${Math.round(auditReport.disk_free_bytes / (1024 * 1024 * 1024))} GB free`, badge: "success", label: "Available" });
    }
    if (auditReport.checks && auditReport.checks.length > 0) {
      for (const check of auditReport.checks) {
        items.push({
          name: check.name,
          value: check.message || (check.status === "passed" ? "Passed" : "Failed"),
          badge: check.status === "passed" ? "success" : "error",
          label: check.status === "passed" ? "Passed" : "Failed",
        });
      }
    }
    return items;
  }, [auditReport]);

  const detectionFailed = detectionItems.filter((item) => item.badge === "error").length;

  // Steps depend on how the server is connected (a headless project's
  // frontends are added after setup, CO-73): the agent path ends
  // at Review, because Commerce Ops plans the setup on the server itself;
  // the SSH path still needs the server inspection, the paths on the
  // server and the health checks the atomic template runs.
  type StepId = "repository" | "magento" | "server" | "inspect" | "deployment" | "health" | "review";
  const STEPS = useMemo(() => {
    const ids: StepId[] = ["repository", "magento", "server"];
    if (serverMethod === "ssh") ids.push("inspect", "deployment", "health");
    ids.push("review");
    const labels: Record<StepId, string> = {
      repository: "Repository",
      magento: "Magento setup",
      server: "Server",
      inspect: "Server check",
      deployment: "Deployment paths",
      health: "Health checks",
      review: "Review",
    };
    return ids.map((id, i) => ({ n: i + 1, id, label: labels[id] }));
  }, [serverMethod]);

  const totalSteps = STEPS.length;
  const stepIdAt = (n: number): StepId => STEPS[n - 1]?.id ?? "review";
  const stepNumberOf = (id: StepId): number => STEPS.find((st) => st.id === id)?.n ?? 1;
  const currentStepId = stepIdAt(step);
  const isMagentoConfigStep = currentStepId === "magento";
  const isServerStep = currentStepId === "server";
  const isDetectStep = currentStepId === "inspect";
  const isDeploymentStep = currentStepId === "deployment";
  const isHealthCheckStep = currentStepId === "health";
  const isReviewStep = currentStepId === "review";

  // Connected servers for the agent path, loaded once and refreshed when a
  // server connected from this page reports in.
  const loadServers = useCallback(async () => {
    setServersLoading(true);
    setServersError(null);
    try {
      const res = await api.listServers();
      const list = res.servers ?? [];
      setServers(list);
      setSelectedServerId((prev) => {
        if (prev && list.some((sv) => sv.id === prev)) return prev;
        const firstReady = list.find((sv) => sv.status === "online") ?? list[0];
        return firstReady ? firstReady.id : "";
      });
    } catch (err) {
      setServersError(err instanceof Error ? err.message : "Could not load servers");
    } finally {
      setServersLoading(false);
    }
  }, []);
  useEffect(() => {
    loadServers();
  }, [loadServers]);
  useEffect(() => {
    if (newServerState === "connected" || newServerState === "complete") {
      loadServers();
      if (newServer) setSelectedServerId(newServer.server.id);
    }
  }, [newServerState, newServer, loadServers]);

  async function handleCreateServer(e?: FormEvent) {
    e?.preventDefault?.();
    const name = newServerName.trim();
    if (!name) {
      setErrors({ newServerName: "Name the server." });
      setInvalidAttempt((n) => n + 1);
      return;
    }
    setCreatingServer(true);
    setErrors({});
    try {
      const res = await api.createServer(name);
      setNewServer(res);
      setNewServerState("waiting");
    } catch (err) {
      setErrors({ server: err instanceof Error ? err.message : "Could not create the server" });
    } finally {
      setCreatingServer(false);
    }
  }

  const selectedServer = servers.find((sv) => sv.id === selectedServerId) ?? null;

  // Server inspection over SSH (SSH path only). The agent path needs no
  // inspection here: the agent's own server check already ran and is shown
  // on the server step.
  useEffect(() => {
    if (!isDetectStep) return;
    let cancelled = false;
    setDetectProgress(0);
    setDetectDone(false);
    setInspectLoading(true);
    setInspectError(null);

    async function runInspection() {
      try {
        const secret = authMode === "password" ? password : privateKey;
        const report = await api.inspectServer({
          host: host.trim(),
          port: parseInt(port, 10) || 22,
          username: user.trim(),
          auth_kind: authMode === "password" ? "ssh_password" : "ssh_private_key",
          secret: secret.trim(),
          php_binary: "php",
          // Empty on the first pass: the inspection looks for the store
          // itself and reports where it found it.
          application_root: serverMagentoRoot,
        });
        if (!cancelled) {
          setAuditReport(report);
          if (report.magento_root) setServerMagentoRoot(report.magento_root);
          setDetectProgress(report.checks?.length || 1);
          setDetectDone(true);
        }
      } catch (err) {
        if (!cancelled) {
          setAuditReport(null);
          setInspectError(err instanceof Error ? err.message : "Server inspection failed");
          setDetectDone(true);
        }
      } finally {
        if (!cancelled) setInspectLoading(false);
      }
    }

    runInspection();
    return () => {
      cancelled = true;
    };
  }, [isDetectStep, host, port, user, authMode, password, privateKey, projectName, inspectAttempt]);

  // The title names the step; the only line under one is a consequence the
  // fields cannot show (a failing health check rolls the release back).
  const { title: stepTitle, intro: stepIntro } = useMemo((): { title: string; intro?: string } => {
    switch (currentStepId) {
      case "repository":
        return { title: "Connect the repository" };
      case "magento":
        return { title: "Magento setup" };
      case "server":
        return { title: "Choose the server" };
      case "inspect":
        return { title: "Checking the server" };
      case "deployment":
        return { title: "Paths on the server" };
      case "health":
        return { title: "Health checks", intro: "A failing check rolls the release back automatically." };
      case "review":
      default:
        return { title: "Review and create" };
    }
  }, [currentStepId]);

  // Validation, per step id.
  function validateStep(n: number): Record<string, string> {
    const errs: Record<string, string> = {};
    const id = stepIdAt(n);
    if (id === "repository") {
      if (useCustomRepo) {
        if (!customRepoUrl.trim()) errs.repo = "Enter the repository URL.";
      } else if (!repo) {
        errs.repo = "Select the repository containing your Magento project.";
      }
      // A branch is required only where a real list of branches exists.
      // For a hand-typed repository URL an empty value means "whatever this
      // repository's own default branch is" — which the API resolves from
      // the provider. Demanding a guess here is how "main" ended up
      // recorded for repositories that never had one.
      if (!useCustomRepo && !branch.trim()) errs.branch = "Select a production branch.";
      if (!projectName.trim()) errs.name = "Give the project a name.";

    } else if (id === "magento") {
      if (!domain.trim() || !/^([a-z0-9-]+\.)+[a-z]{2,}$/i.test(domain.trim())) {
        errs.domain = architecture === "headless" ? "Enter the hostname Magento answers on, for example api.yourdomain.com" : "Enter the store’s domain, for example shop.yourdomain.com";
      }
      if (cryptKeyStrategy === "use_existing" && !cryptKeyCustom.trim()) {
        errs.cryptKey = "Enter the existing 32-character Magento encryption key.";
      }
      if (dbStrategy === "external_database" || dbStrategy === "reuse_existing_on_server") {
        if (!dbHost.trim()) errs.dbHost = "Database host is required.";
        if (!dbPort.trim() || isNaN(+dbPort)) errs.dbPort = "Valid database port is required.";
        if (!dbName.trim()) errs.dbName = "Database name is required.";
        if (!dbUser.trim()) errs.dbUser = "Database user is required.";
      }
      if (searchStrategy === "external") {
        if (!searchHost.trim()) errs.searchHost = "Search host is required.";
        if (!searchPort.trim() || isNaN(+searchPort)) errs.searchPort = "Valid search port is required.";
      }
      if (adminFrontName && !/^[a-zA-Z0-9_\-]+$/.test(adminFrontName.trim())) {
        errs.adminFrontName = "The admin URL path can only contain letters, numbers, hyphens and underscores.";
      }
    } else if (id === "server") {
      if (serverMethod === "agent") {
        if (!selectedServerId) errs.server = "Choose a connected server, or connect a new one.";
      } else {
        if (!host.trim()) errs.host = "Enter the hostname or IP address.";
        if (!port.trim() || isNaN(+port) || +port < 1 || +port > 65535) errs.port = "Valid port required.";
        if (!user.trim()) errs.user = "Enter the SSH user.";
        if (authMode === "password" && !password) errs.password = "Enter the password.";
        if (authMode === "key" && !privateKey.trim()) errs.privateKey = "Enter or paste the SSH private key.";
      }
    } else if (id === "health") {
      healthChecks.forEach((hc) => {
        if (hc.enabled && !/^https?:\/\/[^\s]+$/.test(hc.url) && !hc.url.startsWith("/")) {
          errs[hc.id] = "Enter a valid path starting with / or a full URL starting with http:// or https://";
        }
      });
    } else if (id === "review") {
      if (!projectName.trim()) errs.name = "Give the project a name.";
      if (!domain.trim()) errs.domain = architecture === "headless" ? "Enter Magento’s hostname on the Magento setup step." : "Enter the store’s domain on the Magento setup step.";
    }
    return errs;
  }

  function handleNext() {
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setInvalidAttempt((n) => n + 1);
      return;
    }
    setErrors({});
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  }

  function handleBack() {
    setErrors({});
    if (step > 1) {
      setStep(step - 1);
    }
  }

  // The chosen domain drives the Magento base URLs; nobody should have to
  // type the same hostname three times.
  const storeBaseUrl = domain.trim() ? `https://${domain.trim()}/` : "";
  // In a headless project Magento answers on its own hostname (admin, the
  // GraphQL API the frontends call, media); storefront hostnames belong to
  // the frontends and are added with each frontend after setup.
  const domainLabel = architecture === "headless" ? "Magento hostname" : "Store domain";
  // Media is never picked up during setup: a new store starts empty, an
  // existing store's media is imported after setup from the server, and an
  // adopted installation keeps its own pub/media.
  const mediaStrategy: MagentoMediaStrategy =
    installationType === "existing_magento_migration" ? "uploaded_archive" : installationType === "existing_magento_in_place" ? "existing_server" : "new_empty";

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault?.();
    const errs = validateStep(totalSteps);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setInvalidAttempt((n) => n + 1);
      return;
    }

    setCreating(true);
    setErrors({});

    try {
      const slug = slugify(projectName);
      // The repository is recorded the way the WordPress onboarding records
      // it (CO-69, CO-73): a picked repository by its HTTPS clone URL, with
      // the connection it was listed from, which the API verifies and stores
      // as the project's repository access before anything is created. The
      // agent's checkout authenticates over HTTPS; the SSH address this
      // wizard used to record fetched without that credential and failed.
      const repositorySelection: RepositorySelection = {
        choice: "connect",
        manual: useCustomRepo,
        manualUrl: customRepoUrl,
        repository: repo,
        branch,
      };
      const repository = repositoryFields(repositorySelection, git);

      // 1. Create project with its repository and repository access. An
      // empty branch (a typed URL with no branch) means "the repository's
      // own default branch". No frontend is created here: frontends are
      // added after the Magento setup (CO-73).
      const createdProject = await api.createProject({
        name: projectName.trim(),
        slug,
        repository_url: repository.repository_url,
        default_branch: repository.default_branch ?? "",
        ...(repository.repository_access ? { repository_access: repository.repository_access } : {}),
        architecture,
      });

      // 2. Create the environment first: runtime configuration, secrets and
      // variables are all scoped to it. The agent path is the Commerce Ops
      // managed stack; the SSH path is an existing Magento server.
      const deploymentMode: DeploymentMode = serverMethod === "agent" ? "managed_docker" : "existing_atomic";
      const createdEnv = await api.createEnvironment(createdProject.id, {
        name: environment,
        slug: slugify(environment),
        kind: environment === "Staging" ? "staging" : "production",
        deployment_mode: deploymentMode,
        rollback_policy: "automatic_code_rollback_when_safe",
      });

      // 3. Magento runtime configuration for that environment. Secret values
      // travel write-only and are stored in the secret store; the saved
      // configuration only carries references. A failure here is reported,
      // not swallowed: a project without its configuration cannot deploy.
      const appsRes = await api.listProjectApplications(createdProject.id);
      const backendApp = appsRes.applications?.find((a) => a.type === "magento_backend" || a.application_type === "magento_backend");
      if (backendApp) {
        try {
          await api.saveMagentoRuntimeConfig(
            createdProject.id,
            backendApp.id,
            {
              environment_id: createdEnv.id,
              installation_type: installationType,
              mage_mode: mageMode,
              database: {
                strategy: dbStrategy,
                host: dbHost,
                port: parseInt(dbPort, 10) || 3306,
                database_name: dbName,
                username: dbUser,
                table_prefix: dbTablePrefix || undefined,
                tls_mode: dbTlsMode,
                import_source: (dbStrategy === 'import_into_managed' || dbStrategy === 'import_into_existing') ? dbImportSource : undefined,
              },
              crypt_key: {
                strategy: cryptKeyStrategy,
                verified: cryptKeyStrategy === 'generate_new' || !!cryptKeyCustom,
              },
              urls: {
                base_url: storeBaseUrl,
                secure_base_url: storeBaseUrl,
              },
              admin: {
                front_name: adminFrontName,
                mode: 'custom',
              },
              cache: {
                strategy: cacheStrategy,
                same_instance_for_page: true,
                same_instance_for_session: true,
                default_cache_db: defaultCacheDb,
                page_cache_db: pageCacheDb,
                session_db: sessionDb,
                host: cacheHost,
                port: parseInt(cachePort, 10) || 6379,
                tls: cacheTls,
              },
              search: {
                mode: searchMode,
                engine: searchEngine,
                strategy: searchStrategy,
                host: searchHost,
                port: parseInt(searchPort, 10) || 9200,
                scheme: searchScheme,
                username: searchUsername,
                index_prefix: searchIndexPrefix,
              },
              queue: {
                enabled: queueEnabled,
                strategy: queueStrategy,
                host: queueHost,
                port: parseInt(queuePort, 10) || 5672,
                username: queueUsername,
                virtual_host: queueVirtualHost,
                tls: queueTls,
              },
              media: {
                strategy: mediaStrategy,
              },
              locale: {
                default_locale: defaultLocale,
                timezone: timezone,
                base_currency: baseCurrency,
                default_display_currency: defaultDisplayCurrency,
              },
              custom_env_vars: magentoCustomEnvVars,
            },
            {
              database_password: dbPassword || undefined,
              crypt_key: cryptKeyStrategy === 'use_existing' ? cryptKeyCustom : undefined,
            },
            createdEnv.id,
          );
        } catch (cause) {
          throw new Error(
            `The project and its ${environment} environment were created, but the Magento configuration could not be saved: ${
              cause instanceof Error ? cause.message : String(cause)
            }. Open the project to complete it.`,
          );
        }
      }

      // 4. A typed repository URL carries no verified access; a token entered
      // on this page is stored as the project's Git connection so
      // deployments can read it. A failure is reported, not swallowed: the
      // project then cannot check its code out.
      const token = providerTokens[provider];
      if (useCustomRepo && token) {
        const account = providerAccounts[provider] || "";
        if (account) {
          try {
            await api.configureGitConnection(createdProject.id, { provider, account, token });
          } catch (cause) {
            throw new Error(
              `The project was created, but its repository access could not be saved: ${
                cause instanceof Error ? cause.message : String(cause)
              }. Configure it under Repositories & build.`,
            );
          }
        }
      }

      if (serverMethod === "ssh") {
        // 6a. SSH path: the server connection and the paths on the server.
        try {
          await api.configureServerConnection(createdEnv.id, {
            host: host.trim(),
            port: parseInt(port, 10) || 22,
            username: user.trim(),
            auth_kind: authMode === "password" ? "ssh_password" : "ssh_private_key",
            secret: authMode === "password" ? password : privateKey,
          });
        } catch {
          // non-fatal during batch wizard setup; the environment page reports it
        }
        try {
          // The directory the inspection found (or the operator corrected),
          // never one derived from the project name.
          const appRoot = serverMagentoRoot.trim();
          await api.configureAtomicTemplate(createdEnv.id, {
            application_root: appRoot,
            release_root: appRoot.endsWith("/current") ? `${appRoot.slice(0, -"/current".length)}/releases` : `${appRoot}/releases`,
            shared_paths: sharedPaths,
            php_binary: "php",
            smoke_test_paths: healthChecks
              .filter((h) => h.enabled)
              .map((h) => {
                try {
                  return new URL(h.url).pathname;
                } catch {
                  return "/";
                }
              }),
          });
        } catch {
          // non-fatal
        }
      } else if (selectedServerId) {
        // 6b. Agent path: ask Commerce Ops to plan the setup on the chosen
        // server right away, with the answers already given here, and open
        // the project on that plan — the next step of the workflow is to
        // review and approve it (CO-73). A planning failure is carried to
        // the setup page and shown there, not dropped: the project exists
        // either way and can be planned again from that page.
        let planError: string | null = null;
        try {
          await api.generateOnboardingPlan(createdProject.id, {
            environment_id: createdEnv.id,
            server_id: selectedServerId,
            user_inputs: {
              strategy: "commerceops_docker",
              production_domain: domain.trim(),
            },
          });
        } catch (cause) {
          planError = cause instanceof Error ? cause.message : String(cause);
        }
        navigate(`/app/projects/${createdProject.id}/setup?env=${encodeURIComponent(createdEnv.slug)}`, {
          state: planError ? { planError } : null,
        });
        return;
      }

      navigate(`/app/projects/${createdProject.id}`);
    } catch (cause) {
      setErrors({
        submit: cause instanceof Error ? cause.message : String(cause),
      });
    } finally {
      setCreating(false);
    }
  }

  function handleAddShared() {
    const val = newShared.trim().replace(/^\/+/, "");
    if (val && !sharedPaths.includes(val)) {
      setSharedPaths([...sharedPaths, val]);
      setNewShared("");
    }
  }

  function handleRemoveShared(path: string) {
    setSharedPaths(sharedPaths.filter((p) => p !== path));
  }

  function handleToggleHealthCheck(id: string) {
    setHealthChecks(healthChecks.map((h) => (h.id === id ? { ...h, enabled: !h.enabled } : h)));
  }

  function handleAddHealthCheck() {
    const newId = `hc_${Date.now()}`;
    setHealthChecks([
      ...healthChecks,
      {
        id: newId,
        name: "Custom check",
        url: `https://${domain}/custom-endpoint`,
        expect: "HTTP 200",
        enabled: true,
        fixed: false,
      },
    ]);
  }

  function handleRemoveHealthCheck(id: string) {
    setHealthChecks(healthChecks.filter((h) => h.id !== id));
  }

  async function handleTestConnection() {
    if (!host.trim()) {
      setTestError("Hostname or IP address is required.");
      setTestStatus("error");
      return;
    }
    if (!user.trim()) {
      setTestError("SSH user is required.");
      setTestStatus("error");
      return;
    }
    const secret = authMode === "password" ? password : privateKey;
    if (!secret.trim()) {
      setTestError(authMode === "password" ? "SSH password is required." : "SSH private key is required.");
      setTestStatus("error");
      return;
    }

    setTestStatus("testing");
    setTestError("");
    try {
      const res = await api.testServerConnection({
        host: host.trim(),
        port: parseInt(port, 10) || 22,
        username: user.trim(),
        auth_kind: authMode === "password" ? "ssh_password" : "ssh_private_key",
        secret: secret.trim(),
      });
      if (res.verified) {
        setTestStatus("ok");
        setTestLatency(res.latency_ms ?? null);
      } else {
        setTestStatus("error");
        setTestError(res.message || res.reason || "SSH connection failed");
      }
    } catch (err) {
      setTestStatus("error");
      setTestError(err instanceof Error ? err.message : "SSH connection failed");
    }
  }


  // Branches come only from the chosen repository (CO-51, CO-71). With no
  // repository there is nothing to offer: the field says so instead of
  // listing "main"/"develop" while the value that would be sent is empty.
  const selectedRepoObj = repositories.find((r) => r.fullName === repo);
  const branchOptions = selectedRepoObj?.branches ?? [];


  const isConnected = connectedProviders[provider];

  const waitingForNewAgent = isServerStep && serverMethod === "agent" && connectingNewServer && !!newServer && (newServerState === "waiting" || newServerState === "expired");
  const dbFieldsShown = dbStrategy === "external_database" || dbStrategy === "reuse_existing_on_server" || dbStrategy === "import_into_existing";

  const reviewRows: { label: string; value: string; stepId: StepId; mono: boolean }[] = [
    { label: "Project type", value: architecture === "headless" ? "Headless: Magento backend" : "Magento with its own storefront", stepId: "repository", mono: false },
    { label: architecture === "headless" ? "Magento repository" : "Repository", value: useCustomRepo ? customRepoUrl : repo, stepId: "repository", mono: true },
    { label: architecture === "headless" ? "Magento branch" : "Branch", value: branch.trim() || REPOSITORY_DEFAULT_BRANCH, stepId: "repository", mono: !!branch.trim() },
    {
      label: "Store starts as",
      value:
        installationType === "new_installation"
          ? "New store"
          : installationType === "existing_magento_migration"
          ? "Existing store, moved here from a database dump and media"
          : "Magento already on the server, adopted in place",
      stepId: "magento",
      mono: false,
    },
    { label: "Magento mode", value: mageMode, stepId: "magento", mono: true },
    {
      label: "Database",
      value:
        dbStrategy === "create_managed"
          ? "MariaDB run by Commerce Ops"
          : dbStrategy === "external_database"
          ? `Your database at ${dbHost}`
          : dbStrategy === "reuse_existing_on_server"
          ? `Database already on the server (${dbName})`
          : dbStrategy === "import_into_managed"
          ? "MariaDB run by Commerce Ops; the dump is imported after setup"
          : `Database on the server (${dbName}); the dump is imported after setup`,
      stepId: "magento",
      mono: false,
    },
    { label: "Encryption key", value: cryptKeyStrategy === "generate_new" ? "New key generated by Commerce Ops" : "Existing key from the store", stepId: "magento", mono: false },
    {
      label: "Cache and sessions",
      value: cacheStrategy === "commerceops_managed" ? "Redis run by Commerce Ops" : cacheStrategy === "reuse_existing" ? "Redis already on the server" : cacheStrategy === "external" ? "Your Redis" : "Files (no Redis)",
      stepId: "magento",
      mono: false,
    },
    {
      label: "Catalog search",
      value:
        searchMode === "discovered"
          ? "Detected from the repository"
          : searchStrategy === "application_provided"
            ? "Set by hand: runs inside Magento, no search service"
            : `Set by hand: ${searchEngine === "opensearch" ? "OpenSearch" : "Elasticsearch"} ${searchStrategy === "commerceops_managed" ? "run by Commerce Ops" : searchStrategy === "reuse_existing" ? "already on the server" : "that you run"} · index prefix ${searchIndexPrefix}`,
      stepId: "magento",
      mono: false,
    },
    { label: "Message queue", value: queueEnabled ? "RabbitMQ run by Commerce Ops" : "Magento’s built-in queue (MySQL)", stepId: "magento", mono: false },
    {
      label: "Media files",
      value: mediaStrategy === "new_empty" ? "Start empty" : mediaStrategy === "existing_server" ? "Keep the pub/media already on the server" : "Imported after setup from the server",
      stepId: "magento",
      mono: false,
    },
    {
      label: "Server",
      value:
        serverMethod === "agent"
          ? selectedServer
            ? `${selectedServer.name}${selectedServer.hostname ? ` (${selectedServer.hostname})` : ""} · via the Commerce Ops Agent`
            : "No server chosen"
          : `${user}@${host}:${port} · over SSH`,
      stepId: "server",
      mono: false,
    },
    ...(serverMethod === "ssh"
      ? [
          {
            label: "Found on the server",
            value: auditReport
              ? [
                  auditReport.magento_version ? `Magento ${auditReport.magento_version}` : null,
                  auditReport.php_version ? `PHP ${auditReport.php_version}` : null,
                  auditReport.mysql_version || null,
                  auditReport.opensearch_detected ? "OpenSearch" : null,
                  auditReport.redis_detected ? "Redis" : null,
                  auditReport.varnish_detected ? "Varnish" : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Compatible stack detected"
              : "Inspection pending",
            stepId: "inspect" as StepId,
            mono: false,
          },
          { label: "Shared paths", value: sharedPaths.join(", "), stepId: "deployment" as StepId, mono: true },
          { label: "Health checks", value: healthChecks.filter((h) => h.enabled).map((h) => h.name).join(", "), stepId: "health" as StepId, mono: false },
        ]
      : []),
  ];

  return (
    <OnboardingLayout
      breadcrumbs={[{ label: 'Projects', to: '/app/projects' }, { label: 'New project' }]}
      steps={STEPS.map((s) => ({ key: s.id, label: s.label }))}
      current={step - 1}
      onSelectStep={(index) => {
        setErrors({});
        setStep(STEPS[index]!.n);
      }}
      stepsLabel="New project steps"
      outside={<GitConnectModal git={git} />}
      title={stepTitle}
      intro={stepIntro}
      invalidAttempt={invalidAttempt}
      cancel={{ onClick: () => navigate("/app/projects") }}
      onBack={step > 1 && !isDetectStep ? handleBack : undefined}
      primary={
        <button
          type="button"
          className="button button--primary"
          onClick={handleNext}
          disabled={(isDetectStep && !detectDone) || creating || waitingForNewAgent}
        >
          {creating ? (
            <>
              <Icon name="spinner" size={16} /> Creating project
            </>
          ) : isReviewStep ? (
            serverMethod === "agent" ? "Create project and plan the setup" : "Create project"
          ) : (
            "Continue"
          )}
        </button>
      }
    >
      {/* Step 1: Repository */}
      {step === 1 && (
        <>
          <PlatformChoice selected="magento" />
          <OnboardingChoice label="Project type">
            <OptionCard
              title="Magento with its own storefront"
              selected={architecture === "traditional"}
              selectedLabel="Selected"
              onSelect={() => {
                setArchitecture("traditional");
                setErrors({});
              }}
            />
            <OptionCard
              title="Headless: Magento + frontend app"
              selected={architecture === "headless"}
              selectedLabel="Selected"
              onSelect={() => {
                setArchitecture("headless");
                setErrors({});
              }}
            />
          </OnboardingChoice>

          <div className="onboarding-stack">
            <span className="onboarding-label">{architecture === "headless" ? "Magento repository (backend)" : "Magento repository"}</span>
            <GitProviderChoice
              git={git}
              onChange={() => {
                setRepo("");
                setErrors({});
              }}
            />

            {useCustomRepo ? (
              <ManualRepositoryUrl
                git={git}
                inputId="wizard-repo-url"
                value={customRepoUrl}
                placeholderName="magento-store"
                error={errors.repo}
                onChange={(url) => {
                  setCustomRepoUrl(url);
                  setErrors({});
                }}
                onBack={() => setUseCustomRepo(false)}
                onVerified={(res) => {
                  if (res.default_branch) setBranch(res.default_branch);
                }}
              />
            ) : (
              <div className="onboarding-stack" data-onboarding-invalid={errors.repo ? "true" : undefined}>
                {!isConnected ? (
                  <ProviderConnectPrompt git={git} onManual={() => setUseCustomRepo(true)} />
                ) : (
                  <>
                    <RepositoryList
                      git={git}
                      selected={useCustomRepo ? "" : repo}
                      onSelect={(r) => {
                        setRepo(r.fullName);
                        setUseCustomRepo(false);
                        setErrors({});
                      }}
                      hint={(r) =>
                        r.magento ? (
                          <span className="onboarding-status onboarding-status--success onboarding-status--small">
                            <Icon name="check" size={16} />
                            <span>Magento detected</span>
                          </span>
                        ) : null
                      }
                    />
                    <div className="onboarding-meta-row">
                      <button type="button" className="button button--ghost button--small" onClick={() => setUseCustomRepo(true)}>
                        Enter manual URL
                      </button>
                    </div>
                  </>
                )}
                <FieldError message={errors.repo} />
              </div>
            )}
          </div>

          <div className="onboarding-grid">
            <Field id="wizard-branch" label="Production branch" error={errors.branch}>
              {useCustomRepo ? (
                <Input
                  type="text"
                  className="onboarding-mono"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  placeholder="the repository's default branch"
                  spellCheck={false}
                  autoComplete="off"
                />
              ) : !selectedRepoObj ? (
                <Select value="" disabled>
                  <option value="">Choose a repository first</option>
                </Select>
              ) : (
                <Select
                  className="onboarding-mono"
                  value={branch}
                  onChange={(e) => {
                    setBranch(e.target.value);
                    clearError("branch");
                  }}
                >
                  {branchOptions.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field id="wizard-magento-root" label="Magento root in repository" hint="Where composer.json lives. Use / when Magento is root.">
              <Input type="text" className="onboarding-mono" value={magentoRoot} onChange={(e) => setMagentoRoot(e.target.value)} />
            </Field>
          </div>

          <Field
            id="wizard-project-name"
            label="Project name"
            className="onboarding-field--short"
            hint={projectName ? `URL slug: ${slugify(projectName)}` : undefined}
            error={errors.name}
          >
            <Input
              type="text"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                clearError("name");
              }}
              placeholder="e.g. Acme Store"
            />
          </Field>

          {architecture === "headless" && (
            <Callout tone="info" role="note" testId="wizard-frontends-later" title="Frontends are added after the Magento setup, under Repositories & build." />
          )}
        </>
      )}

      {/* Step 2: Magento runtime and infrastructure */}
      {isMagentoConfigStep && (
        <>
          {/* Store domain: drives the Magento base URLs, the HTTPS certificate and the health checks. */}
          <Section title="Domain and admin path">
            <div className="onboarding-grid">
              <Field
                id="wizard-domain"
                label={domainLabel}
                error={errors.domain}
                hint={
                  <>
                    Base URL {storeBaseUrl ? <code>{storeBaseUrl}</code> : "https://…/"}. Point its DNS at the server.
                  </>
                }
              >
                <Input
                  type="text"
                  className="onboarding-mono"
                  value={domain}
                  onChange={(e) => {
                    setDomain(e.target.value);
                    clearError("domain");
                  }}
                  placeholder={architecture === "headless" ? "api.yourdomain.com" : "shop.yourdomain.com"}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
              <Field
                id="wizard-admin-path"
                label="Admin URL path"
                error={errors.adminFrontName}
                hint={`Opens at /${adminFrontName || "admin"}`}
              >
                <Input
                  type="text"
                  className="onboarding-mono"
                  value={adminFrontName}
                  onChange={(e) => {
                    setAdminFrontName(e.target.value);
                    clearError("adminFrontName");
                  }}
                  placeholder="admin"
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
            </div>
          </Section>

          <Section title="How this store starts">
            <OptionGroup label="How this store starts">
              {[
                { id: "new_installation" as MagentoInstallationType, title: "New store", desc: undefined },
                { id: "existing_magento_migration" as MagentoInstallationType, title: "Move an existing store here", desc: "Imports the database dump and media; keeps the store’s encryption key." },
                { id: "existing_magento_in_place" as MagentoInstallationType, title: "Adopt Magento already on the server", desc: undefined },
              ].map((t) => (
                <OptionCard
                  key={t.id}
                  title={t.title}
                  description={t.desc}
                  selected={installationType === t.id}
                  selectedLabel="Selected"
                  onSelect={() => {
                    setInstallationType(t.id);
                    if (t.id === "new_installation") {
                      setCryptKeyStrategy("generate_new");
                      setDbStrategy("create_managed");
                    } else {
                      setCryptKeyStrategy("use_existing");
                      if (t.id === "existing_magento_migration") {
                        setDbStrategy("import_into_managed");
                      } else {
                        setDbStrategy("reuse_existing_on_server");
                      }
                    }
                  }}
                />
              ))}
            </OptionGroup>

            <div className="field">
              <span className="field__label" aria-hidden="true">Magento mode</span>
              <Segmented
                label="Magento mode"
                value={mageMode}
                onChange={setMageMode}
                options={[
                  { value: "production", label: "Production" },
                  { value: "developer", label: "Developer" },
                  { value: "default", label: "Default" },
                ]}
              />
              <span className="field__hint">MAGE_MODE</span>
            </div>
          </Section>

          <Section title="Database">
            <Field id="wizard-db-strategy" label="Where the database runs">
              <Select value={dbStrategy} onChange={(e) => setDbStrategy(e.target.value as MagentoDatabaseStrategy)}>
                <option value="create_managed">MariaDB run by Commerce Ops on the server (recommended)</option>
                <option value="import_into_managed">MariaDB run by Commerce Ops, filled from a database dump</option>
                <option value="reuse_existing_on_server">A database that already exists on the server</option>
                <option value="import_into_existing">A database on the server, filled from a database dump</option>
                <option value="external_database">A database you run elsewhere (RDS, Aurora, Galera, …)</option>
              </Select>
            </Field>

            {dbFieldsShown && (
              <div className="onboarding-grid onboarding-grid--compact">
                <Field id="wizard-db-host" label="Host" error={errors.dbHost}>
                  <Input type="text" className="onboarding-mono" value={dbHost} onChange={(e) => { setDbHost(e.target.value); clearError("dbHost"); }} placeholder="127.0.0.1" />
                </Field>
                <Field id="wizard-db-port" label="Port" error={errors.dbPort}>
                  <Input type="text" inputMode="numeric" className="onboarding-mono" value={dbPort} onChange={(e) => { setDbPort(e.target.value); clearError("dbPort"); }} placeholder="3306" />
                </Field>
                <Field id="wizard-db-name" label="Database name" error={errors.dbName}>
                  <Input type="text" className="onboarding-mono" value={dbName} onChange={(e) => { setDbName(e.target.value); clearError("dbName"); }} placeholder="magento" />
                </Field>
                <Field id="wizard-db-user" label="Database user" error={errors.dbUser}>
                  <Input type="text" className="onboarding-mono" value={dbUser} onChange={(e) => { setDbUser(e.target.value); clearError("dbUser"); }} placeholder="magento" />
                </Field>
                <Field id="wizard-db-password" label="Password">
                  <Input type="password" value={dbPassword} onChange={(e) => setDbPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
                </Field>
                <Field id="wizard-db-prefix" label="Table prefix (optional)">
                  <Input type="text" className="onboarding-mono" value={dbTablePrefix} onChange={(e) => setDbTablePrefix(e.target.value)} placeholder="mg_" />
                </Field>
                <Field id="wizard-db-tls" label="TLS">
                  <Select value={dbTlsMode} onChange={(e) => setDbTlsMode(e.target.value)}>
                    <option value="disable">Off</option>
                    <option value="require">Required</option>
                    <option value="verify_ca">Required, verify the CA</option>
                    <option value="verify_full">Required, verify the CA and hostname</option>
                  </Select>
                </Field>
              </div>
            )}

            {(dbStrategy === "import_into_managed" || dbStrategy === "import_into_existing") && (
              <Callout tone="info">
                Import the dump after setup: <strong>Recovery → Import an existing store</strong>. Copy it to the server first (for example <code>/srv/handover/prod.sql.gz</code>) and import it before the first deployment.
              </Callout>
            )}
          </Section>

          <Section title="Encryption key">
            <OptionGroup label="Encryption key">
              <OptionCard
                title="Generate a new key"
                selected={cryptKeyStrategy === "generate_new"}
                selectedLabel="Selected"
                onSelect={() => setCryptKeyStrategy("generate_new")}
              />
              <OptionCard
                title="Use the store’s existing key"
                selected={cryptKeyStrategy === "use_existing"}
                selectedLabel="Selected"
                onSelect={() => setCryptKeyStrategy("use_existing")}
              />
            </OptionGroup>

            {cryptKeyStrategy === "use_existing" && (
              <Field id="wizard-crypt-key" label="Existing key (the crypt/key value from app/etc/env.php)" error={errors.cryptKey}>
                <Input
                  type="password"
                  className="onboarding-mono"
                  value={cryptKeyCustom}
                  onChange={(e) => {
                    setCryptKeyCustom(e.target.value);
                    clearError("cryptKey");
                  }}
                  placeholder="e.g. 7a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d"
                  autoComplete="off"
                />
              </Field>
            )}

            <Callout tone="warning" title="Why this matters">
              Magento encrypts passwords, payment tokens and API credentials in the database with this key. Using a different key on an existing database makes that data unreadable for good.
            </Callout>
          </Section>

          <Section title="Cache and sessions (Redis)">
            <Field id="wizard-cache-strategy" label="Where Redis runs">
              <Select value={cacheStrategy} onChange={(e) => setCacheStrategy(e.target.value as MagentoCacheStrategy)}>
                <option value="commerceops_managed">Redis run by Commerce Ops (recommended)</option>
                <option value="reuse_existing">Redis already on the server</option>
                <option value="external">Redis you run elsewhere</option>
                <option value="disabled">No Redis — cache and sessions in files</option>
              </Select>
            </Field>
            <dl className="onboarding-facts">
              <div>
                <dt>Default cache</dt>
                <dd className="onboarding-mono">Database 0</dd>
              </div>
              <div>
                <dt>Page cache (FPC)</dt>
                <dd className="onboarding-mono">Database 1</dd>
              </div>
              <div>
                <dt>Sessions</dt>
                <dd className="onboarding-mono">Database 2</dd>
              </div>
            </dl>
          </Section>

          {/* Catalog search: discovered from the code, override only on request */}
          <Section
            title="Catalog search"
            testId="wizard-catalog-search"
            actions={
              <Checkbox
                checked={searchMode === "override"}
                onChange={(e) => setSearchMode(e.target.checked ? "override" : "discovered")}
                data-testid="wizard-search-override"
                label="Set the search engine by hand"
              />
            }
          >
            {searchMode === "discovered" ? (
              <p className="onboarding-note" data-testid="wizard-search-discovered">
                Detected from the repository when the setup plan is generated.
              </p>
            ) : (
              <>
                <p className="onboarding-note">Applied as written, even if the repository’s code points elsewhere.</p>
                <div className="onboarding-grid onboarding-grid--compact">
                  <Field id="wizard-search-strategy" label="Search service">
                    <Select value={searchStrategy} onChange={(e) => setSearchStrategy(e.target.value as MagentoSearchStrategy)}>
                      <option value="commerceops_managed">Run by Commerce Ops</option>
                      <option value="reuse_existing">Already on the server</option>
                      <option value="external">A cluster you run elsewhere</option>
                      <option value="application_provided">None: search runs inside Magento (module-provided engine)</option>
                    </Select>
                  </Field>
                  {searchStrategy !== "application_provided" ? (
                    <>
                      <Field id="wizard-search-engine" label="Engine">
                        <Select value={searchEngine} onChange={(e) => setSearchEngine(e.target.value as MagentoSearchEngine)}>
                          <option value="opensearch">OpenSearch 2.x</option>
                          <option value="elasticsearch">Elasticsearch</option>
                        </Select>
                      </Field>
                      <Field id="wizard-search-prefix" label="Index prefix">
                        <Input type="text" className="onboarding-mono" value={searchIndexPrefix} onChange={(e) => setSearchIndexPrefix(e.target.value)} placeholder="magento2_" />
                      </Field>
                    </>
                  ) : null}
                </div>
              </>
            )}
          </Section>

          <Section title="Queue, media and locale">
            <div className="onboarding-grid">
              <Checkbox
                checked={queueEnabled}
                onChange={(e) => setQueueEnabled(e.target.checked)}
                label="Message queue (RabbitMQ)"
                hint={queueEnabled ? undefined : "Magento uses its built-in queue in the database."}
              />
              <div className="field">
                <span className="field__label">Media files (pub/media)</span>
                <span className="field__hint">
                  {installationType === "existing_magento_migration"
                    ? "Imported after setup: Recovery → Import an existing store."
                    : installationType === "existing_magento_in_place"
                      ? "Stays where it is on the server"
                      : "Starts empty"}
                </span>
              </div>
            </div>
            <fieldset className="onboarding-fieldset">
              <legend className="field__label">Locale, currency and time zone</legend>
              <div className="onboarding-grid onboarding-grid--compact">
                <Field id="wizard-locale" label="Locale">
                  <Input type="text" className="onboarding-mono" value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)} placeholder="en_US" />
                </Field>
                <Field id="wizard-currency" label="Currency">
                  <Input type="text" className="onboarding-mono" value={baseCurrency} onChange={(e) => setBaseCurrency(e.target.value)} placeholder="USD" />
                </Field>
                <Field id="wizard-timezone" label="Time zone">
                  <Input type="text" className="onboarding-mono" value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="UTC" />
                </Field>
              </div>
            </fieldset>
          </Section>
        </>
      )}

      {/* Server step */}
      {isServerStep && (
        <>
          <OnboardingChoice label="How should Commerce Ops connect to the server?">
            <OptionCard
              testId="server-method-agent"
              title={
                <span className="onboarding-provider">
                  <Icon name="server" size={16} />
                  Commerce Ops Agent
                </span>
              }
              badge={<StatusBadge kind="capability" value="recommended" />}
              description="Connects outbound over HTTPS; no inbound port, no SSH credentials stored. Runs Magento and its services as containers."
              selected={serverMethod === "agent"}
              selectedLabel="Selected"
              onSelect={() => {
                setServerMethod("agent");
                setErrors({});
              }}
            />
            <OptionCard
              testId="server-method-ssh"
              title={
                <span className="onboarding-provider">
                  <Icon name="lock" size={16} />
                  SSH to an existing Magento server
                </span>
              }
              description="For Magento already running natively on the server (own PHP, MySQL and web server). Connects over SSH with a deploy user and switches releases atomically; nothing is containerized."
              selected={serverMethod === "ssh"}
              selectedLabel="Selected"
              onSelect={() => {
                setServerMethod("ssh");
                setErrors({});
              }}
            />
          </OnboardingChoice>

          {serverMethod === "agent" ? (
            !connectingNewServer ? (
              <Section>
                <div className="onboarding-stack" data-onboarding-invalid={errors.server && !selectedServerId && servers.length === 0 ? "true" : undefined}>
                  {serversLoading ? (
                    <p role="status" className="onboarding-status">
                      <Icon name="spinner" size={16} /> Loading servers…
                    </p>
                  ) : servers.length === 0 ? (
                    <p className="onboarding-note">No servers connected</p>
                  ) : (
                    <Field id="wizard-server" label="Connected servers" error={errors.server}>
                      <Select
                        value={selectedServerId}
                        onChange={(e) => {
                          setSelectedServerId(e.target.value);
                          setErrors({});
                        }}
                      >
                        {servers.map((sv) => (
                          <option key={sv.id} value={sv.id}>
                            {sv.name}
                            {sv.hostname ? ` (${sv.hostname})` : ""} — {serverStatusLabel[sv.status] ?? sv.status}, {readinessStatusLabel[sv.readiness_status] ?? sv.readiness_status}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  {serversError && <FieldError message={serversError} />}
                  {servers.length === 0 ? <FieldError message={errors.server} /> : null}

                  {selectedServer && (
                    <div className="onboarding-summary" data-testid="selected-server-summary">
                      <dl className="onboarding-facts">
                        <div>
                          <dt>Agent</dt>
                          <dd>{serverStatusLabel[selectedServer.status] ?? selectedServer.status}</dd>
                        </div>
                        <div>
                          <dt>Server check</dt>
                          <dd>{readinessStatusLabel[selectedServer.readiness_status] ?? selectedServer.readiness_status}</dd>
                        </div>
                        <div>
                          <dt>Operating system</dt>
                          <dd>{selectedServer.os ? `${selectedServer.os}${selectedServer.architecture ? ` (${selectedServer.architecture})` : ""}` : "Detected on connect"}</dd>
                        </div>
                        <div>
                          <dt>Docker</dt>
                          <dd>
                            {(() => {
                              const cap = selectedServer.capabilities?.find((c) => c.id === "docker.runtime");
                              if (!cap) return "Checked on connect";
                              return cap.status === "supported" ? "Installed" : cap.status === "blocked" ? "Not available" : "Not checked";
                            })()}
                          </dd>
                        </div>
                      </dl>
                      <p className="onboarding-meta-row">
                        <Link to={`/app/servers/${selectedServer.id}`} target="_blank" rel="noreferrer" className="onboarding-link">
                          Open the full server check
                          <Icon name="external-link" size={16} />
                          <span className="visually-hidden"> (opens in a new tab)</span>
                        </Link>
                        {selectedServer.status !== "online" && (
                          <span>Setup cannot run until the agent reconnects.</span>
                        )}
                      </p>
                    </div>
                  )}

                  <div>
                    <button type="button" className="button button--secondary" onClick={() => setConnectingNewServer(true)}>
                      <Icon name="plus" size={16} />
                      Connect a new server
                    </button>
                  </div>
                </div>
              </Section>
            ) : !newServer ? (
              <Section>
                <form onSubmit={handleCreateServer} className="onboarding-stack" noValidate>
                  <Field
                    id="wizard-new-server-name"
                    label="Server name"
                    className="onboarding-field--short"
                    error={errors.newServerName ?? errors.server}
                  >
                    <Input
                      type="text"
                      value={newServerName}
                      onChange={(e) => {
                        setNewServerName(e.target.value);
                        clearError("newServerName");
                      }}
                      placeholder="e.g. prod-web-01"
                      autoComplete="off"
                      spellCheck={false}
                      autoFocus
                    />
                  </Field>
                  <div className="onboarding-inline">
                    <button type="submit" className="button button--secondary" disabled={creatingServer}>
                      {creatingServer ? "Generating" : "Generate install command"}
                    </button>
                    <button type="button" className="button button--ghost" onClick={() => setConnectingNewServer(false)}>
                      Back to the list
                    </button>
                  </div>
                </form>
              </Section>
            ) : (
              <>
                <AgentInstallPanel
                  serverId={newServer.server.id}
                  installationCommand={newServer.installation_command}
                  onStateChange={(st) => setNewServerState(st)}
                />
                <div className="onboarding-meta-row">
                  {agentConnected(newServerState) ? (
                    <span className="onboarding-status onboarding-status--success">
                      <Icon name="check-circle" size={16} />
                      <span>{newServer.server.name} connected</span>
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => {
                      setConnectingNewServer(false);
                      setNewServer(null);
                      setNewServerName("");
                      loadServers();
                    }}
                  >
                    Choose a different server
                  </button>
                </div>
                <FieldError message={errors.server} />
              </>
            )
          ) : (
            <Section>
              <div className="onboarding-grid onboarding-grid--ssh">
                <Field id="ssh-host" label="Hostname or IP address" error={errors.host}>
                  <Input
                    type="text"
                    className="onboarding-mono"
                    value={host}
                    onChange={(e) => {
                      setHost(e.target.value);
                      setTestStatus("idle");
                      clearError("host");
                    }}
                    placeholder="203.0.113.10"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </Field>
                <Field id="ssh-port" label="SSH port" error={errors.port}>
                  <Input
                    type="text"
                    inputMode="numeric"
                    className="onboarding-mono"
                    value={port}
                    onChange={(e) => {
                      setPort(e.target.value);
                      clearError("port");
                    }}
                  />
                </Field>
                <Field id="ssh-user" label="SSH user" error={errors.user}>
                  <Input
                    type="text"
                    className="onboarding-mono"
                    value={user}
                    onChange={(e) => {
                      setUser(e.target.value);
                      clearError("user");
                    }}
                    placeholder="deploy"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </Field>
              </div>

              <div className="field">
                <span className="field__label" aria-hidden="true">Authentication</span>
                <Segmented
                  label="Authentication"
                  value={authMode}
                  onChange={setAuthMode}
                  options={[
                    { value: "key", label: "SSH private key" },
                    { value: "password", label: "Password" },
                  ]}
                />
              </div>

              {authMode === "key" ? (
                <Field
                  id="ssh-key"
                  label="SSH private key (PEM or OpenSSH format)"
                  error={errors.privateKey}
                  hint="Stored encrypted; used only to connect to this server."
                >
                  <Textarea
                    rows={5}
                    className="onboarding-mono"
                    value={privateKey}
                    onChange={(e) => {
                      setPrivateKey(e.target.value);
                      setTestStatus("idle");
                      clearError("privateKey");
                    }}
                    placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----"}
                    spellCheck={false}
                    autoComplete="off"
                  />
                </Field>
              ) : (
                <Field id="ssh-password" label="SSH password" error={errors.password} hint="Stored encrypted and used only to connect to this server." className="onboarding-field--short">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setTestStatus("idle");
                      clearError("password");
                    }}
                    placeholder="••••••••••••"
                    autoComplete="off"
                  />
                </Field>
              )}

              <div className="onboarding-test-row">
                <button type="button" className="button button--secondary" onClick={handleTestConnection} disabled={testStatus === "testing"}>
                  {testStatus === "testing" ? <Icon name="spinner" size={16} /> : null}
                  {testStatus === "ok" ? "Test again" : "Test connection"}
                </button>
                <div role="status" className="onboarding-test-row__result">
                  {testStatus === "testing" && <span className="onboarding-status">Connecting over SSH…</span>}
                  {testStatus === "ok" && (
                    <span className="onboarding-status onboarding-status--success">
                      <Icon name="check-circle" size={16} />
                      Connected · <span className="onboarding-mono">{user}@{host}:{port}{testLatency !== null ? ` · ${testLatency} ms` : ""}</span>
                    </span>
                  )}
                  {testStatus === "error" && (
                    <span className="onboarding-status onboarding-status--danger">
                      <Icon name="alert-circle" size={16} />
                      <span>{testError || "Commerce Ops could not connect over SSH."}</span>
                    </span>
                  )}
                </div>
              </div>
            </Section>
          )}
        </>
      )}

      {/* SSH path: detect the environment */}
      {isDetectStep && (
        <>
          {inspectError && (
            <Callout
              tone="danger"
              title="The inspection failed"
              testId="inspect-failed"
              actions={
                <button type="button" className="button button--secondary button--small" onClick={() => setInspectAttempt((value) => value + 1)}>
                  Retry
                </button>
              }
            >
              <p>{inspectError}</p>
              <p>Nothing was changed on the server.</p>
            </Callout>
          )}
          <div className="onboarding-list">
            <div className="onboarding-list__head">
              {/* The failure is told once, by the callout above; the head keeps the count when there is one. */}
              {inspectError ? null : (
                <span className={`onboarding-status${detectDone && detectionFailed === 0 ? " onboarding-status--success" : ""}`} role="status">
                  <Icon name={detectDone ? (detectionFailed > 0 ? "alert-circle" : "check-circle") : "spinner"} size={16} />
                  {detectDone
                    ? detectionItems.length === 0
                      ? "Inspection complete · nothing was reported"
                      : detectionFailed > 0
                        ? `Inspection complete · ${detectionFailed} of ${detectionItems.length} failed`
                        : `Inspection complete · ${detectionItems.length}/${detectionItems.length} read`
                    : "Inspecting server over SSH…"}
                </span>
              )}
              <span className="onboarding-mono onboarding-muted">
                {user}@{host}
              </span>
            </div>

            {detectionItems.length > 0 && (
              <ul className="onboarding-list__rows">
                {detectionItems.map((item, idx) => {
                  const resolved = idx < detectProgress;
                  return (
                    <li key={item.name} className="onboarding-list__row onboarding-list__row--detect">
                      <span className="onboarding-list__label">{item.name}</span>
                      <span className={`onboarding-mono onboarding-list__value${resolved ? "" : " onboarding-muted"}`}>{resolved ? item.value : "—"}</span>
                      {resolved ? (
                        <span className={`onboarding-status onboarding-status--small ${item.badge === "error" ? "onboarding-status--danger" : "onboarding-status--success"}`}>
                          <Icon name={item.badge === "error" ? "alert-circle" : "check-circle"} size={16} />
                          {item.label}
                        </span>
                      ) : (
                        <span className="onboarding-status onboarding-status--small">Detecting…</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

        </>
      )}

      {/* SSH path: deployment configuration */}
      {isDeploymentStep && (
        <>
          <Section>
            <dl className="onboarding-facts">
              <div>
                <dt>Branch</dt>
                <dd>{branch.trim() ? <span className="onboarding-mono">{branch}</span> : REPOSITORY_DEFAULT_BRANCH}</dd>
              </div>
              <div>
                <dt>How releases are switched</dt>
                <dd>Atomic releases</dd>
              </div>
              <div>
                <dt>Directory on the server</dt>
                <dd className="onboarding-mono">/var/www/{slugify(projectName || "magento")}</dd>
              </div>
            </dl>
          </Section>

          <div className="onboarding-list">
            <div className="onboarding-list__head">
              <span className="onboarding-list__title">Shared files and directories</span>
            </div>
            <div className="onboarding-list__body">
              <ul className="onboarding-chips" aria-label="Shared files and directories">
                {sharedPaths.map((p) => (
                  <li key={p} className="onboarding-chip">
                    <span className="onboarding-mono">{p}</span>
                    <IconButton icon="close" label={`Remove ${p}`} variant="ghost" size="sm" onClick={() => handleRemoveShared(p)} />
                  </li>
                ))}
              </ul>
              <div className="onboarding-inline">
                <Input
                  type="text"
                  className="onboarding-mono"
                  placeholder="Add path (e.g. pub/custom)"
                  aria-label="Add a shared path"
                  value={newShared}
                  onChange={(e) => setNewShared(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddShared())}
                />
                <button type="button" className="button button--secondary" onClick={handleAddShared}>
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* The atomic template is fixed by design: shown, not editable. */}
          <div className="onboarding-list">
            <div className="onboarding-list__head">
              <span className="onboarding-list__title">What every deployment does</span>
              <span className="onboarding-muted">Fixed sequence — no custom hooks</span>
            </div>
            <ol className="onboarding-list__rows">
              {DEFAULT_PHASES.map((p, i) => (
                <li key={p.id} className="onboarding-list__row onboarding-list__row--phase">
                  <span className="onboarding-mono onboarding-muted">{String(i + 1).padStart(2, "0")}</span>
                  <span className="onboarding-list__text">
                    <span className="onboarding-list__name">{p.name}</span>
                    <code className="onboarding-list__code">{p.cmd}</code>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}

      {/* SSH path: health checks */}
      {isHealthCheckStep && (
        <>
          <div className="onboarding-list">
            <ul className="onboarding-list__rows">
              {healthChecks.map((hc) => (
                <li key={hc.id} className="onboarding-list__row onboarding-list__row--health">
                  <Input
                    type="checkbox"
                    checked={hc.enabled}
                    onChange={() => handleToggleHealthCheck(hc.id)}
                    aria-label={`Run the ${hc.name} check`}
                  />
                  <span className={`onboarding-list__name${hc.enabled ? "" : " onboarding-muted"}`}>{hc.name}</span>
                  <Input
                    type="text"
                    className="onboarding-mono onboarding-list__url"
                    value={hc.url}
                    aria-label={`${hc.name} URL`}
                    aria-invalid={errors[hc.id] ? true : undefined}
                    aria-describedby={errors[hc.id] ? `${hc.id}-error` : undefined}
                    onChange={(e) => {
                      const val = e.target.value;
                      setHealthChecks(healthChecks.map((x) => (x.id === hc.id ? { ...x, url: val } : x)));
                      clearError(hc.id);
                    }}
                  />
                  <span className="onboarding-mono onboarding-muted">{hc.expect}</span>
                  {!hc.fixed ? (
                    <IconButton icon="trash" label={`Remove the ${hc.name} check`} variant="ghost" size="sm" onClick={() => handleRemoveHealthCheck(hc.id)} />
                  ) : (
                    <span />
                  )}
                  {errors[hc.id] ? (
                    <span className="onboarding-list__error">
                      <FieldError id={`${hc.id}-error`} message={errors[hc.id]} />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="onboarding-list__body">
              <button type="button" className="button button--secondary button--small" onClick={handleAddHealthCheck}>
                <Icon name="plus" size={16} />
                Add URL check
              </button>
            </div>
          </div>

        </>
      )}

      {/* Review step */}
      {isReviewStep && (
        <>
          <Section>
            <div className="onboarding-grid">
              <Field id="review-project-name" label="Project name" error={errors.name}>
                <Input
                  type="text"
                  required
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    clearError("name");
                  }}
                />
              </Field>

              <Field id="review-environment" label="First environment">
                <Select value={environment} onChange={(e) => setEnvironment(e.target.value as "Production" | "Staging")}>
                  <option value="Production">Production</option>
                  <option value="Staging">Staging</option>
                </Select>
              </Field>

              <div className="field" data-onboarding-invalid={errors.domain ? "true" : undefined}>
                <span className="field__label">{domainLabel}</span>
                <span className="onboarding-mono onboarding-value">{domain || "—"}</span>
                <FieldError message={errors.domain} />
              </div>
            </div>
          </Section>

          <div className="onboarding-list">
            <ul className="onboarding-list__rows">
              {reviewRows.map((row) => (
                <li key={row.label} className="onboarding-list__row onboarding-list__row--review">
                  <span className="onboarding-list__label">{row.label}</span>
                  <span className={`onboarding-list__value onboarding-list__value--truncate${row.mono ? " onboarding-mono" : ""}`} title={row.value}>
                    {row.value}
                  </span>
                  <button
                    type="button"
                    onClick={() => setStep(stepNumberOf(row.stepId))}
                    className="button button--ghost button--small"
                    aria-label={`Edit ${row.label.toLowerCase()}`}
                  >
                    Edit
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {errors.submit && (
            <Callout tone="danger" title="The project could not be created">
              {errors.submit}
            </Callout>
          )}

          <Callout tone="info" title="What happens when you create the project">
            {serverMethod === "agent"
              ? `Saves the configuration and plans the setup of ${environment} on ${selectedServer?.name ?? "the chosen server"}. Nothing is installed until you approve the plan, run the setup and deploy the first release.`
              : `Saves the configuration, the SSH connection and the paths. Deployments stay in rehearsal mode until an owner turns on live execution for ${environment}.`}
          </Callout>
        </>
      )}
    </OnboardingLayout>
  );
}
